// Globals
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { createFeed, deleteFeed, getExportFeeds, getFeed, getFeeds, regenerateFeed, updateFeed } from '../../feature';

// Components
import {
  StyledContentBlock,
  StyledHeaderWrapper,
  StyledPageTitle,
  StyledPageWrapper,
} from './FeedsMainContainerStyles';
import { Button, Drawer } from '@/ui';
import { PlusIcon } from '@/icons';
import { FeedForm, FeedsList } from '../../components';
import { showSuccessToast, useForm } from '@/components';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { IFeed } from '@/models/feeds.model';
import { SupportedLocales } from '@/models/localization.model';

// Hooks
import { useModal } from '@/hooks';
import { useGetFeeds } from '../../hooks';
import { TFeedFormValues } from '../../helpers/types';
import { CreateFeedDTO, GetFeedDTO, UpdateFeedDTO } from '../../dtos';
import { fileDownload, toNumber } from '@/utils';

const FeedsMainContainer = () => {
  const { t } = useTranslation(['seo']);
  const { canManage } = usePermissions();
  const canManageFeeds = canManage(Resource.FEED);
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { isLoading, feeds, pagination, query } = useGetFeeds();
  const [editableFeed, setEditableFeed] = useState<IFeed | null>(null);
  const { isOpened: isCreateFeedOpened, openModal: openCreateFeedModal, closeModal: closeCreateFeedModal } = useModal();

  const handleChangePage = (page: number) => {
    const dto = new GetFeedDTO({ ...query, page });
    dispatch(getFeeds(dto));
  };

  const handleClose = () => {
    setEditableFeed(null);
    closeCreateFeedModal();
  };

  const handleEdit = async (metaTag: IFeed) => {
    try {
      const response = await dispatch(
        getFeed({
          id: metaTag.id,
        }),
      ).unwrap();

      setEditableFeed(response.data);
      openCreateFeedModal();
    } catch (error) {}
  };

  const handleDelete = async (item: IFeed) => {
    try {
      await dispatch(
        deleteFeed({
          id: item.id,
        }),
      ).unwrap();
      await dispatch(getFeeds({ page: 1 }));
    } catch (error) {}
  };

  const refreshEditableFeed = async (feedId: string) => {
    try {
      const response = await dispatch(getFeed({ id: feedId })).unwrap();
      setEditableFeed(response.data);
      dispatch(getFeeds({ page: pagination?.current_page ?? 1 }));
    } catch (error) {}
  };

  const handleRegenerateFeed = async () => {
    if (!editableFeed?.id) return;
    try {
      await dispatch(
        regenerateFeed({
          feed_id: editableFeed?.id,
        }),
      ).unwrap();
      await dispatch(getFeeds({ page: 1 }));
      showSuccessToast({ message: t('feeds_regenerate_started') });
      await refreshEditableFeed(editableFeed.id);
    } catch (error) {}
  };

  const handleUpsertMetaTag = async (data: TFeedFormValues) => {
    try {
      if (editableFeed?.id) {
        const dto = new UpdateFeedDTO({
          id: editableFeed.id,
          ...data,
        });

        const response = await dispatch(updateFeed(dto)).unwrap();
        showSuccessToast({ message: t('seo_data_saved') });
        await dispatch(getFeeds({ page: 1 }));
        setEditableFeed(response.data);
      } else {
        const dto = new CreateFeedDTO(data);
        const response = await dispatch(createFeed(dto)).unwrap();
        showSuccessToast({ message: t('feed_generation_data_save') });
        await dispatch(getFeeds({ page: 1 }));
        setEditableFeed(response.data);
        openCreateFeedModal();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleExport = useCallback(
    async (locale: SupportedLocales) => {
      if (!editableFeed) return;
      try {
        const downloadFilename = editableFeed?.files?.[locale]?.split('/').pop() || 'file.xml';
        const file = await dispatch(
          getExportFeeds({
            feed_id: toNumber(editableFeed.id),
            locale: SupportedLocales.UK,
          }),
        ).unwrap();

        fileDownload(file, downloadFilename, 'xml');
      } catch (error) {}
    },
    [dispatch, editableFeed],
  );

  return (
    <StyledPageWrapper>
      <StyledHeaderWrapper>
        <StyledPageTitle>{t('feeds_page_title')}</StyledPageTitle>
        {canManageFeeds && (
          <Button icon={<PlusIcon />} onClick={openCreateFeedModal}>
            {t('feeds_page_create')}
          </Button>
        )}
      </StyledHeaderWrapper>
      <StyledContentBlock>
        <FeedsList
          isLoading={isLoading}
          currentPage={pagination?.current_page ?? 1}
          onPageChange={handleChangePage}
          total={pagination?.total ?? 0}
          list={feeds ?? []}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </StyledContentBlock>

      <Drawer
        open={isCreateFeedOpened || !!editableFeed}
        onClose={handleClose}
        destroyOnClose
        width="80%"
        title={t(editableFeed ? 'feeds_edit' : 'feeds_add')}
      >
        <FeedForm
          onSubmit={handleUpsertMetaTag}
          handleRegenerateFeed={handleRegenerateFeed}
          isLoading={isLoading}
          initialValues={editableFeed}
          formInstance={form}
          handleExport={handleExport}
          onRefresh={editableFeed?.id ? () => refreshEditableFeed(editableFeed.id) : undefined}
        />
      </Drawer>
    </StyledPageWrapper>
  );
};

export default FeedsMainContainer;
